import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';

import { API_ENDPOINT } from './../_config/config';

export type MediaImportStatus =
    'queued' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'rollback' | 'rolling_back' | 'deleting';

/** One export loaded into a project (the API's media-imports rows, as invenire-v2's) */
export interface MediaImport {
    id: string;
    fileName: string;
    status: MediaImportStatus;
    progress: number;
    totalFileCount: number;
    totalFileSize: number;
    startedAt: string;
    completedAt: string | null;
    durationMs: number | null;
    errorMessage: string | null;
    jobId: string | null;
    attempts: number;
    /** 's3' when the file was put in the bucket directly, 'upload' when uploaded in Invenire */
    source: string;
    hasSourceJson: boolean;
}

export interface MediaImportSummary {
    counts: Partial<Record<MediaImportStatus, number>>;
    completedFileCount: number;
    completedFileSize: number;
}

/** invenire-v2's status labels */
export const MEDIA_IMPORT_STATUS_LABELS: Record<MediaImportStatus, string> = {
    queued: 'Queued',
    processing: 'Processing',
    completed: 'Completed',
    failed: 'Failed',
    cancelled: 'Cancelled',
    rollback: 'Rolled Back',
    rolling_back: 'Rolling Back',
    deleting: 'Removing',
};

/** An import something is still working on (its row changes on its own) */
export function isImportActive(status: MediaImportStatus) {
    return ['queued', 'processing', 'rolling_back', 'deleting'].includes(status);
}

/** Largest export an upload may be (invenire-v2's MAX_IMPORT_FILE_BYTES; the API checks it too) */
export { MAX_IMPORT_FILE_BYTES } from '../_utils/check-export';

/** SHA-256 of a file's bytes in hex, as the Lambda hashes the export it reads */
export async function sha256Hex(file: Blob) {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
}

/** Where and how to PUT an export (a presigned S3 URL) */
export interface UploadTarget {
    url: string;
    method: 'PUT';
    headers: Record<string, string>;
}

/** As invenire-v2: under a second in ms, else seconds to two places; '—' when unknown */
export function formatDurationMs(ms: number | null) {
    if(ms == null) { return '—'; }
    return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`;
}

export function plural(count: number, word: string) {
    return `${count} ${word}${count === 1 ? '' : 's'}`;
}

/** Run a task on each item, at most `limit` at a time */
export async function runPool<T>(items: T[], limit: number, task: (item: T) => Promise<unknown>) {
    let next = 0;
    const worker = async () => {
        while(next < items.length) { await task(items[next++]); }
    };
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

async function sameContents(a: Blob, b: Blob) {
    if(a.size !== b.size) { return false; }
    const [x, y] = await Promise.all([a.arrayBuffer(), b.arrayBuffer()]);
    const bytesB = new Uint8Array(y);
    return new Uint8Array(x).every((byte, i) => byte === bytesB[i]);
}

/**
 * Split picked files into those to add and those with the same bytes as one already picked (or
 * picked before it), as invenire-v2 does; bytes rather than a hash, as crypto.subtle is HTTPS-only
 */
export async function dropDuplicateContents(selected: File[], picked: File[]) {
    const kept = [...selected];
    const added: File[] = [];
    const skipped: { file: File, sameAs: File }[] = [];
    for(const file of picked) {
        let sameAs: File;
        for(const other of kept) {
            if(await sameContents(file, other)) { sameAs = other; break; }
        }
        if(sameAs) {
            skipped.push({ file, sameAs });
        } else {
            kept.push(file);
            added.push(file);
        }
    }
    return { added, skipped };
}

export type MediaImportSortBy = 'startedAt' | 'completedAt' | 'fileName' | 'status' | 'totalFileCount' | 'totalFileSize';

/** What the list endpoint takes: sizes in bytes, durations in seconds, days as YYYY-MM-DD (UTC, inclusive) */
export interface MediaImportQuery {
    search?: string;
    status?: MediaImportStatus[];
    /** the project's media (`<projectId>-<label>`) the imports came from */
    mediaId?: string[] | string;
    minFiles?: number;
    maxFiles?: number;
    minSize?: number;
    maxSize?: number;
    startedFrom?: string;
    startedTo?: string;
    completedFrom?: string;
    completedTo?: string;
    minDuration?: number;
    maxDuration?: number;
    sortBy?: MediaImportSortBy;
    sortOrder?: 'asc' | 'desc';
    limit: number;
    offset: number;
}

/** The query as request (or URL) params: lists comma-separated, blank ones left out */
export function toParams(query: Partial<MediaImportQuery>) {
    const params: Record<string, string | number> = {};
    for(const [key, value] of Object.entries(query)) {
        const param = Array.isArray(value) ? value.join(',') : value;
        if(param != null && param !== '') { params[key] = param; }
    }
    return params;
}

@Injectable({
    providedIn: 'root'
})
export class MediaImportService {
    /** requests that skip the app's interceptors: S3 refuses a presigned PUT that also carries a token */
    private direct: HttpClient;

    constructor(private http: HttpClient, backend: HttpBackend) {
        this.direct = new HttpClient(backend);
    }

    /** Start an upload: the API records it (queued) and gives the URL to PUT the export to */
    createUpload(projectId: string, file: { fileName: string, size: number, sha256: string }) {
        return this.http.post<{ import: MediaImport, upload: UploadTarget }>(`${this.url(projectId)}/upload`, file);
    }

    /** PUT the export to its presigned URL, with progress events */
    putFile(url: string, file: Blob, headers: Record<string, string>) {
        return this.direct.put(url, file, { headers, reportProgress: true, observe: 'events' });
    }

    hash(file: Blob) {
        return sha256Hex(file);
    }

    private url(projectId: string) {
        return `${API_ENDPOINT}/projects/${projectId}/media-imports`;
    }

    /** A page of the project's imports, searched, filtered and sorted (newest first unless sortBy says) */
    list(projectId: string, query: MediaImportQuery) {
        return this.http.get<{ imports: MediaImport[], count: number }>(this.url(projectId), { params: toParams(query) });
    }

    summary(projectId: string) {
        return this.http.get<MediaImportSummary>(`${this.url(projectId)}/summary`);
    }

    get(projectId: string, importId: string) {
        return this.http.get<{ import: MediaImport }>(`${this.url(projectId)}/${importId}`);
    }

    /** The export's JSON as the API streams it */
    source(projectId: string, importId: string) {
        return this.http.get(`${this.url(projectId)}/${importId}/source`, { responseType: 'blob' });
    }

    /**
     * Cancel (queued or processing), dismiss (failed) or roll back (completed) an import; the
     * row comes back as it is now (null once a cancelled upload is gone)
     */
    act(projectId: string, importId: string, action: 'cancel' | 'dismiss' | 'rollback') {
        return this.http.post<{ import: MediaImport | null, message: string }>(`${this.url(projectId)}/${importId}/${action}`, {});
    }

    /** Remove an import that no longer runs: what it added, its JSON and its row */
    delete(projectId: string, importId: string) {
        return this.http.delete<{ import: MediaImport, message: string }>(`${this.url(projectId)}/${importId}`);
    }
}

export type MediaImportAction = 'cancel' | 'dismiss' | 'rollback' | 'delete';

/** What a row offers: the actions the API allows in its status */
export function importActions(status: MediaImportStatus): MediaImportAction[] {
    switch(status) {
        case 'queued': case 'processing': return ['cancel'];
        case 'completed': return ['rollback', 'delete'];
        case 'failed': return ['dismiss', 'delete'];
        case 'cancelled': case 'rollback': return ['delete'];
        default: return [];
    }
}
